/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { skillsUndistribute } from "../funcs/skillsUndistribute.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UndistributeSkillRequest,
  UndistributeSkillSecurity,
} from "../models/operations/undistributeskill.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UndistributeSkillMutationVariables = {
  request: UndistributeSkillRequest;
  security?: UndistributeSkillSecurity | undefined;
  options?: RequestOptions;
};

export type UndistributeSkillMutationData = void;

export type UndistributeSkillMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * undistribute skills
 *
 * @remarks
 * Revoke a skill's active distribution to exactly one plugin or assistant. Repeated requests are a no-op.
 */
export function useUndistributeSkillMutation(
  options?: MutationHookOptions<
    UndistributeSkillMutationData,
    UndistributeSkillMutationError,
    UndistributeSkillMutationVariables
  >,
): UseMutationResult<
  UndistributeSkillMutationData,
  UndistributeSkillMutationError,
  UndistributeSkillMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUndistributeSkillMutation(client, options),
    ...options,
  });
}

export function mutationKeyUndistributeSkill(): MutationKey {
  return ["@gram/client", "skills", "undistribute"];
}

export function buildUndistributeSkillMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UndistributeSkillMutationVariables,
  ) => Promise<UndistributeSkillMutationData>;
} {
  return {
    mutationKey: mutationKeyUndistributeSkill(),
    mutationFn: function undistributeSkillMutationFn({
      request,
      security,
      options,
    }): Promise<UndistributeSkillMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(skillsUndistribute(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
